import React from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { IAppReduxStore } from '../../../redux/store';
import { TooltipPlacement } from '../AppTooltip/AppTooltip';
import AppIcon from './AppIcon';
import { SxProps } from '@mui/material';
import { GridValidRowModel } from '@mui/x-data-grid-pro';

/**
 * Component that shows a Status symbol for a document in the DocTable.
 */
export default ({
    type,
    document,
    tooltip,
    tooltipPlacement,
    sx,
    withLabel,
    labelSx = {},
}: IDocStatusIconProps) => {
    /** Configure component translation. */
    const { i18n } = useTranslation();

    /** Access relevant state variables from the redux store. */
    const docStatusDefs = useSelector((store: IAppReduxStore) => store.projectDocStatus.data);

    let documentType: string = type;

    if (type?.toLowerCase() === 'workflow' && document?.ewfSignStatus === '0') {
        documentType = 'Rejected';
    }

    if (document?.JobBackend) {
        documentType = 'AutomatedJob';
    }

    /** Try to find a matching DocStatus. */
    const def = (docStatusDefs ?? []).find(x => x.name === documentType);

    /** Unknown DocStatus - render nothing. */
    if (!def) {
        return <></>;
    }

    /** Deconstruct DocStatus object. */
    const { icon, color, translations } = def;

    /** Determine the tooltip text, if needed. */
    const tooltipText = tooltip || tooltipPlacement ? translations[i18n.language] : null;

    return (
        <AppIcon
            {...{
                path: icon,
                color,
                tooltipText,
                tooltipPlacement,
                sx,
                withLabel,
                labelSx,
                label: translations?.[i18n.language] ?? def.name,
            }}
        />
    );
};

/**
 * Interface that describes the props for a DocStatusIcon component.
 */
export interface IDocStatusIconProps {
    /** Doc Status Type. */
    type: string;

    /** (Optional) Document. */
    document?: GridValidRowModel;

    /** (Optional) Whether to show a tooltip or not. */
    tooltip?: boolean;

    /** (Optional) Icon Tooltip Placement. */
    tooltipPlacement?: TooltipPlacement;

    /** (Optional) Extra styles to be applied to the component. */
    sx?: SxProps;

    /** (Optional) Defines Typography component. */
    withLabel?: boolean;

    /** (Optional) Defines Typography's extra styles. */
    labelSx?: SxProps;
}
